import React from 'react';
import ReactDOM from 'react-dom/client';
import { MsalProvider } from '@azure/msal-react';
import './index.css';
import AppShell from './AppShell';
import AuthGate from './AuthGate';
import MaintenanceGate from './MaintenanceGate';
import { AppProvider } from './appContext';
import { msalInstance } from './authConfig';
import CustomerAuthGate, { PORTAL_BASENAME } from './portal/CustomerAuthGate';

// Canonical domain: psops.aumentumtech.com is the original custom domain, kept alive
// indefinitely so old bookmarks/links don't 404 (same comment on the API's CORS allowlist,
// apps/api/src/main.ts) -- both hostnames serve this exact same Azure Static Web App, so this is
// a plain client-side bounce rather than a DNS change. Runs before MSAL or React initialize, so
// nothing boots on the old host. Skipped in a popup/iframe (MSAL's own redirect/silent-renewal
// windows) so an in-flight login isn't interrupted mid-handshake.
if (
  window.location.hostname === 'psops.aumentumtech.com' &&
  window.top === window &&
  !window.opener
) {
  window.location.replace(
    `https://pulse.aumentumtech.com${window.location.pathname}${window.location.search}${window.location.hash}`
  );
}

// Which world this page load belongs to, decided by URL before anything mounts. /portal/* is the
// customer portal (local credentials, ADR 0035); everything else is the internal app behind Entra
// SSO. One build serves both — see ADR 0034 on why there is no second deployment — but the two
// auth stacks must never both initialize, so this is a hard branch rather than a runtime fallback.
const isPortal =
  window.location.pathname === PORTAL_BASENAME ||
  window.location.pathname.startsWith(`${PORTAL_BASENAME}/`);

// Fail-loud startup check for the Power Automate flow URLs still in use.
// REACT_APP_* vars are inlined by CRA at BUILD time. If a secret is missing from
// the build environment, the constant becomes undefined and fetch(undefined, ...)
// silently POSTs to the app's own origin — producing a 405 instead of hitting the
// flow. This surfaces exactly which vars are missing so that failure is diagnosable.
//
// Only two left. SOW extraction, SOW save and the project list are now the `ai`, `intake` and
// `core` modules in apps/api; their flow URLs (REACT_APP_SOW_FLOW_URL, REACT_APP_SOW_SAVE_URL,
// REACT_APP_GET_PROJECTS_URL) were removed with the legacy paths on 2026-07-27. Folder and
// customer-subsite creation have no replacement yet, so they stay.
const REQUIRED_FLOW_ENV_VARS = [
  'REACT_APP_CREATE_FOLDERS_URL',
  'REACT_APP_CREATE_CUSTOMER_SUBSITE_URL',
];
const missingFlowEnvVars = REQUIRED_FLOW_ENV_VARS.filter(
  name => !process.env[name]
);
if (missingFlowEnvVars.length > 0) {
  console.error(
    `[Pulse] Missing Power Automate flow URL(s): ${missingFlowEnvVars.join(', ')}. ` +
      'These REACT_APP_* vars were not present at build time, so the related fetch calls will ' +
      "POST to the app's own domain and fail with 405. Set them as build environment " +
      'variables (GitHub Actions secrets → the env: block in the Azure workflow) and redeploy.'
  );
}

// Same fail-loud treatment for the Entra IDs. Without these the app can't sign anyone in at all,
// so this is more severe than a missing flow URL — AuthGate renders an explanatory screen rather
// than letting MSAL throw something opaque.
const missingAuthEnvVars = ['REACT_APP_ENTRA_TENANT_ID', 'REACT_APP_ENTRA_CLIENT_ID'].filter(
  name => !process.env[name]
);
if (missingAuthEnvVars.length > 0) {
  console.error(
    `[Pulse] Missing Entra ID config: ${missingAuthEnvVars.join(', ')}. ` +
      'Sign-in is disabled until these are set — see apps/web/.env.example.'
  );
}

// Any window that only exists to carry an auth response back to the main app must NOT render.
// Two of them reach this file, and both used to be missed in part:
//   - popups (`window.opener`), which started a second app that opened a popup from a popup;
//   - MSAL v5's silent-renewal bridge iframe (`window.parent`), which booted the entire 293KB app
//     -- AuthGate, AppProvider, its own API calls -- inside a hidden frame that has 10s
//     (`iframeBridgeTimeout`) to post the response back before MSAL throws `timed_out`.
// handleRedirectPromise below does the actual handing-back; rendering is pure waste in both.
const isAuthResponseWindow =
  typeof window !== 'undefined' &&
  /[?#&](code|error)=/.test(window.location.hash + window.location.search) &&
  ((!!window.opener && window.opener !== window) || window.parent !== window);

const root = ReactDOM.createRoot(document.getElementById('root'));

// The portal never touches MSAL — no Entra initialization, no redirect handling, no MsalProvider.
// A customer contact has no account in the tenant, so booting MSAL for them would at best be dead
// weight and at worst throw on a tenant it can't reach.
if (isPortal) {
  root.render(
    <React.StrictMode>
      {/* Same ordering rule as the internal tree below: the gate first, because AppProvider's
          fetches need a token, then the shell AppShell picks (always ExternalApp here, since
          /auth/me answers with an ExternalPrincipal for a customer token). */}
      <CustomerAuthGate>
        <AppProvider>
          <MaintenanceGate>
            <AppShell />
          </MaintenanceGate>
        </AppProvider>
      </CustomerAuthGate>
    </React.StrictMode>
  );
} else {
  bootInternalApp();
}

// MSAL must finish initializing (and process any redirect coming back from Entra) before React
// renders, or the first paint can flash the sign-in screen at an already-authenticated user.
function bootInternalApp() {
  msalInstance
    .initialize()
    .then(() => msalInstance.handleRedirectPromise())
    .then(result => {
      // After a redirect login, adopt the returned account; otherwise fall back to whatever's
      // already cached. MSAL doesn't set an active account on its own.
      if (result?.account) {
        msalInstance.setActiveAccount(result.account);
      } else if (!msalInstance.getActiveAccount() && msalInstance.getAllAccounts().length > 0) {
        msalInstance.setActiveAccount(msalInstance.getAllAccounts()[0]);
      }
    })
    .finally(() => {
      // handleRedirectPromise above has already handed the response back; rendering here would
      // start the second app described at `isAuthResponseWindow`.
      if (isAuthResponseWindow) return;
      root.render(
        <React.StrictMode>
          <MsalProvider instance={msalInstance}>
            {/* Order matters: AuthGate must run first (AppProvider's fetches need a token), then
                AppProvider loads the current user + maintenance status, then MaintenanceGate uses
                both to decide whether this user sees the app or a lockout screen. */}
            <AuthGate>
              <AppProvider>
                <MaintenanceGate>
                  <AppShell />
                </MaintenanceGate>
              </AppProvider>
            </AuthGate>
          </MsalProvider>
        </React.StrictMode>
      );
    });
}